import { create } from "zustand";
import { getRelativePathFromCanvas } from "@/utils/utils";
import type { NavTreeNode } from "@/utils/types";
import {
  rehydrateFromSupabase,
  upsertInstances,
  updateInstance,
  deleteInvalidStoryboards,
  deleteInstancesForComponent,
} from "./instancesSupabaseSync";

// ═══════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════

// Data for an instance that requires contentWindow access.
export interface ContentWindowData {
  screenshotUrl?: string; // GCS URL for the uploaded screenshot
  width?: number;
  height?: number;

  // The hotreloadts of the component at the time of capture, since its possible
  // the hotReloadTs is new however at the time we caught the update the
  // contentWindow wasn't available so we couldn't capture it
  captureHotReloadTs?: number;
}

export interface DragData {
  fileContents?: string;
  fileContentsSourceFilename?: string;
  compressedNavTree?: string;
}

/**
 * Info about a selected instance, used for canvas selection sync.
 */
export interface SelectedInstanceInfo {
  instanceKey: string;
  storyboardId: string; // For storyboard group highlighting
  pageId: string; // For page-aware group highlighting
}

/**
 * Fields of Instance that can be updated via updateInstanceField.
 */
export type UpdatableInstanceField = "contentWindowData" | "dragData";

/**
 * A component instance with all its data.
 */
export interface Instance {
  // Identity
  instanceCodebaseId: string;
  storyboardId: string;
  pageId: string;
  uniquePath: string;
  domUniquePath: string;

  // Component info
  componentName: string;
  componentFilepath: string; // Component definition file (e.g., /src/components/Button.tsx)
  instanceFilepath: string; // Where this instance is used (e.g., /src/pages/Dashboard.tsx)
  componentExportName?: string;

  // DOM info
  domCodebaseIds: string[];

  // Runtime props
  props: Record<string, unknown>;

  // Change detection
  hotReloadTs?: number;

  // Captured data (updated async)
  contentWindowData: ContentWindowData;
  dragData: DragData;
}

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Computes a component file key from the filepath and component name.
 * Uses the project-relative path (split on canvasId) to ensure consistency.
 */
export function computeComponentFileKey(
  filepath: string,
  componentName: string,
  canvasId: string,
): string {
  const relativePath = getRelativePathFromCanvas(filepath, canvasId);
  return `${relativePath}:${componentName}`;
}

/**
 * Generates a unique key for an instance based on component identity, storyboardId, and domUniquePath.
 * Uses the project-relative path (split on canvasId) to ensure consistency across environments.
 */
export function getInstanceUniqueKey(
  instance: Instance,
  canvasId: string,
): string {
  const relativePath = getRelativePathFromCanvas(
    instance.componentFilepath,
    canvasId,
  );
  return `${relativePath}:${instance.componentName}-${instance.storyboardId}-${instance.domUniquePath}`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// STORE
// ═══════════════════════════════════════════════════════════════════════════════

interface InstancesState {
  // canvasId -> componentFileKey -> storyboardId -> instanceUniqueKey -> Instance
  // This needs to be serializable for persistence.
  instancesByCanvas: Record<
    string,
    Record<string, Record<string, Record<string, Instance>>>
  >;

  // Separate storage for screenshot data URLs (base64).
  // Keyed by instanceUniqueKey. Kept separate from instances to avoid persisting large strings.
  screenshotDataUrls: Record<string, string>;

  // Separate storage for NavTree nodes (contain circular references).
  // Keyed by instanceUniqueKey. Kept separate to avoid JSON serialization issues.
  navNodes: Record<string, NavTreeNode>;

  // Non-persisted: tracks the first hotReloadTs seen per instance this session.
  // Used to distinguish "page reload" (hotReloadTs === initial) from "actual hot reload".
  initialHotReloadTs: Record<string, number>;

  // Reverse lookup: elementKey -> instance info (enables O(1) lookup from canvas selection)
  elementKeyToInstanceInfo: Record<string, SelectedInstanceInfo>;

  // Info about currently selected instance in canvas (for visual highlight + scroll)
  selectedCanvasInstance: SelectedInstanceInfo | null;

  // Actions

  // Set all instances for a specific storyboard (replaces that storyboard's instances only)
  setInstancesForStoryboard: (
    canvasId: string,
    componentFileKey: string,
    storyboardId: string,
    instances: Instance[],
  ) => void;

  // Clear all instances for storyboards that are no longer valid
  clearInvalidStoryboards: (
    canvasId: string,
    componentFileKey: string,
    validStoryboardIds: Set<string>,
  ) => void;

  clearInstancesForComponent: (
    canvasId: string,
    componentFileKey: string,
  ) => void;

  updateInstanceContentWindowData: (
    canvasId: string,
    componentFileKey: string,
    storyboardId: string,
    instanceKey: string,
    data: ContentWindowData,
  ) => void;

  updateInstanceDragData: (
    canvasId: string,
    componentFileKey: string,
    storyboardId: string,
    instanceKey: string,
    data: DragData,
  ) => void;

  /**
   * Generic field updater for Instance. Type-safe for contentWindowData and dragData.
   */
  updateInstanceField: <K extends UpdatableInstanceField>(
    canvasId: string,
    componentFileKey: string,
    storyboardId: string,
    instanceKey: string,
    field: K,
    data: Partial<Instance[K]>,
  ) => void;

  getInstancesForComponent: (
    canvasId: string,
    componentFileKey: string,
  ) => Instance[];

  // Screenshot data URL actions (stored separately for persistence)
  setScreenshotDataUrl: (instanceKey: string, dataUrl: string) => void;
  getScreenshotDataUrl: (instanceKey: string) => string | undefined;
  clearScreenshotDataUrl: (instanceKey: string) => void;

  // NavNode actions (stored separately to avoid circular reference issues)
  setNavNode: (instanceKey: string, navNode: NavTreeNode) => void;
  getNavNode: (instanceKey: string) => NavTreeNode | undefined;
  clearNavNode: (instanceKey: string) => void;

  // Initial hotReloadTs actions (non-persisted, session-scoped)
  setInitialHotReloadTs: (instanceKey: string, ts: number) => void;
  getInitialHotReloadTs: (instanceKey: string) => number | undefined;

  // Element key reverse lookup actions (for canvas selection sync)
  setElementKeyToInstanceInfo: (
    elementKey: string,
    info: SelectedInstanceInfo,
  ) => void;
  clearElementKeyToInstanceInfo: (elementKey: string) => void;
  setSelectedCanvasInstance: (info: SelectedInstanceInfo | null) => void;

  // Batch actions (single state update for multiple items - better perf)
  setBatchNavNodes: (updates: Record<string, NavTreeNode>) => void;
  setBatchElementKeyToInstanceInfo: (
    updates: Record<string, SelectedInstanceInfo>,
  ) => void;
  setBatchInitialHotReloadTs: (updates: Record<string, number>) => void;

  // Rehydration action - loads data from Supabase for a specific canvas
  // validStoryboardIds: If non-empty, orphaned storyboard instances will be deleted
  rehydrateCanvasData: (
    canvasId: string,
    validStoryboardIds: Set<string>,
  ) => Promise<void>;

  // Merge instances from Supabase into the store.
  // Only merges contentWindowData and dragData for existing instances (Supabase is "stale").
  // For new instances, adds them to the store (can be overwritten by NavTree later).
  mergeSupabaseInstances: (
    canvasId: string,
    componentFileKey: string,
    supabaseData: Record<string, Record<string, Instance>>,
  ) => void;
}

export const useInstancesStore = create<InstancesState>()((set, get) => ({
  instancesByCanvas: {},
  screenshotDataUrls: {},
  navNodes: {},
  initialHotReloadTs: {},
  elementKeyToInstanceInfo: {},
  selectedCanvasInstance: null,

  setInstancesForStoryboard: (
    canvasId,
    componentFileKey,
    storyboardId,
    instances,
  ) => {
    // Build the merged instances first (for both Zustand and Supabase)
    const canvasData = get().instancesByCanvas[canvasId] ?? {};
    const componentData = canvasData[componentFileKey] ?? {};
    const existingStoryboardData = componentData[storyboardId] ?? {};

    const mergedInstances: Instance[] = [];

    if (instances.length > 0) {
      // Merge: new instance data + preserved captured data
      for (const instance of instances) {
        const key = getInstanceUniqueKey(instance, canvasId);
        const existingInstance = existingStoryboardData[key];

        if (existingInstance) {
          mergedInstances.push({
            ...instance,
            contentWindowData: {
              ...existingInstance.contentWindowData,
              ...instance.contentWindowData,
            },
            dragData: {
              ...existingInstance.dragData,
              ...instance.dragData,
            },
          });
        } else {
          mergedInstances.push(instance);
        }
      }
    }

    // Update Zustand state
    set((state) => {
      const canvasData = state.instancesByCanvas[canvasId] ?? {};
      const componentData = canvasData[componentFileKey] ?? {};

      if (instances.length === 0) {
        // Remove this storyboard's entry entirely
        const { [storyboardId]: _, ...remainingStoryboards } = componentData;

        // If no storyboards left, remove the component entry
        if (Object.keys(remainingStoryboards).length === 0) {
          const { [componentFileKey]: __, ...remainingComponents } = canvasData;
          return {
            instancesByCanvas: {
              ...state.instancesByCanvas,
              [canvasId]: remainingComponents,
            },
          };
        }

        return {
          instancesByCanvas: {
            ...state.instancesByCanvas,
            [canvasId]: {
              ...canvasData,
              [componentFileKey]: remainingStoryboards,
            },
          },
        };
      }

      // Build lookup for the new storyboard data
      const newStoryboardData: Record<string, Instance> = {};
      for (const instance of mergedInstances) {
        newStoryboardData[getInstanceUniqueKey(instance, canvasId)] = instance;
      }

      return {
        instancesByCanvas: {
          ...state.instancesByCanvas,
          [canvasId]: {
            ...canvasData,
            [componentFileKey]: {
              ...componentData,
              [storyboardId]: newStoryboardData,
            },
          },
        },
      };
    });

    // Sync to Supabase (fire-and-forget)
    upsertInstances(canvasId, componentFileKey, storyboardId, mergedInstances);
  },

  clearInvalidStoryboards: (canvasId, componentFileKey, validStoryboardIds) => {
    set((state) => {
      const componentData =
        state.instancesByCanvas[canvasId]?.[componentFileKey];
      if (!componentData) return state;

      // Filter to only keep valid storyboards
      const filteredData: Record<string, Record<string, Instance>> = {};
      for (const [storyboardId, instances] of Object.entries(componentData)) {
        if (validStoryboardIds.has(storyboardId)) {
          filteredData[storyboardId] = instances;
        }
      }

      // If nothing changed, return same state
      if (
        Object.keys(filteredData).length === Object.keys(componentData).length
      ) {
        return state;
      }

      // If no storyboards left, remove the component entry
      if (Object.keys(filteredData).length === 0) {
        const { [componentFileKey]: _, ...remainingComponents } =
          state.instancesByCanvas[canvasId];
        return {
          instancesByCanvas: {
            ...state.instancesByCanvas,
            [canvasId]: remainingComponents,
          },
        };
      }

      return {
        instancesByCanvas: {
          ...state.instancesByCanvas,
          [canvasId]: {
            ...state.instancesByCanvas[canvasId],
            [componentFileKey]: filteredData,
          },
        },
      };
    });

    // Sync deletion to Supabase
    deleteInvalidStoryboards(canvasId, componentFileKey, [
      ...validStoryboardIds,
    ]);
  },

  clearInstancesForComponent: (canvasId, componentFileKey) => {
    set((state) => {
      const canvasData = state.instancesByCanvas[canvasId];
      if (!canvasData) return state;

      const { [componentFileKey]: _, ...remainingComponents } = canvasData;

      return {
        instancesByCanvas: {
          ...state.instancesByCanvas,
          [canvasId]: remainingComponents,
        },
      };
    });

    // Sync deletion to Supabase
    deleteInstancesForComponent(canvasId, componentFileKey);
  },

  updateInstanceContentWindowData: (
    canvasId,
    componentFileKey,
    storyboardId,
    instanceKey,
    data,
  ) =>
    get().updateInstanceField(
      canvasId,
      componentFileKey,
      storyboardId,
      instanceKey,
      "contentWindowData",
      data,
    ),

  updateInstanceDragData: (
    canvasId,
    componentFileKey,
    storyboardId,
    instanceKey,
    data,
  ) =>
    get().updateInstanceField(
      canvasId,
      componentFileKey,
      storyboardId,
      instanceKey,
      "dragData",
      data,
    ),

  updateInstanceField: (
    canvasId,
    componentFileKey,
    storyboardId,
    instanceKey,
    field,
    data,
  ) => {
    set((state) => {
      const instance =
        state.instancesByCanvas[canvasId]?.[componentFileKey]?.[storyboardId]?.[
          instanceKey
        ];
      if (!instance) return state;

      return {
        instancesByCanvas: {
          ...state.instancesByCanvas,
          [canvasId]: {
            ...state.instancesByCanvas[canvasId],
            [componentFileKey]: {
              ...state.instancesByCanvas[canvasId][componentFileKey],
              [storyboardId]: {
                ...state.instancesByCanvas[canvasId][componentFileKey][
                  storyboardId
                ],
                [instanceKey]: {
                  ...instance,
                  [field]: {
                    ...instance[field],
                    ...data,
                  },
                },
              },
            },
          },
        },
      };
    });

    // Sync updated instance to Supabase
    const updatedInstance =
      get().instancesByCanvas[canvasId]?.[componentFileKey]?.[storyboardId]?.[
        instanceKey
      ];
    if (updatedInstance) {
      updateInstance(
        canvasId,
        componentFileKey,
        storyboardId,
        instanceKey,
        updatedInstance,
      );
    }
  },

  getInstancesForComponent: (canvasId, componentFileKey) => {
    const componentData = get().instancesByCanvas[canvasId]?.[componentFileKey];
    if (!componentData) return [];

    // Flatten: storyboardId -> instanceKey -> Instance into Instance[]
    const result: Instance[] = [];
    for (const storyboardData of Object.values(componentData)) {
      for (const instance of Object.values(storyboardData)) {
        result.push(instance);
      }
    }
    return result;
  },

  setScreenshotDataUrl: (instanceKey, dataUrl) =>
    set((state) => ({
      screenshotDataUrls: {
        ...state.screenshotDataUrls,
        [instanceKey]: dataUrl,
      },
    })),

  getScreenshotDataUrl: (instanceKey) => get().screenshotDataUrls[instanceKey],

  clearScreenshotDataUrl: (instanceKey) =>
    set((state) => {
      const { [instanceKey]: _, ...rest } = state.screenshotDataUrls;
      return { screenshotDataUrls: rest };
    }),

  setNavNode: (instanceKey, navNode) =>
    set((state) => ({
      navNodes: {
        ...state.navNodes,
        [instanceKey]: navNode,
      },
    })),

  getNavNode: (instanceKey) => get().navNodes[instanceKey],

  clearNavNode: (instanceKey) =>
    set((state) => {
      const { [instanceKey]: _, ...rest } = state.navNodes;
      return { navNodes: rest };
    }),

  setInitialHotReloadTs: (instanceKey, ts) =>
    set((state) => ({
      initialHotReloadTs: {
        ...state.initialHotReloadTs,
        [instanceKey]: ts,
      },
    })),

  getInitialHotReloadTs: (instanceKey) => get().initialHotReloadTs[instanceKey],

  setElementKeyToInstanceInfo: (elementKey, info) =>
    set((state) => ({
      elementKeyToInstanceInfo: {
        ...state.elementKeyToInstanceInfo,
        [elementKey]: info,
      },
    })),

  clearElementKeyToInstanceInfo: (elementKey) =>
    set((state) => {
      const { [elementKey]: _, ...rest } = state.elementKeyToInstanceInfo;
      return { elementKeyToInstanceInfo: rest };
    }),

  setSelectedCanvasInstance: (info) => set({ selectedCanvasInstance: info }),

  // Batch methods - single state update for multiple items
  setBatchNavNodes: (updates) =>
    set((state) => ({
      navNodes: { ...state.navNodes, ...updates },
    })),

  setBatchElementKeyToInstanceInfo: (updates) =>
    set((state) => ({
      elementKeyToInstanceInfo: {
        ...state.elementKeyToInstanceInfo,
        ...updates,
      },
    })),

  setBatchInitialHotReloadTs: (updates) =>
    set((state) => ({
      initialHotReloadTs: { ...state.initialHotReloadTs, ...updates },
    })),

  rehydrateCanvasData: async (
    canvasId: string,
    validStoryboardIds: Set<string>,
  ) => {
    const canvasData = await rehydrateFromSupabase(
      canvasId,
      validStoryboardIds,
    );
    set((state) => ({
      instancesByCanvas: {
        ...state.instancesByCanvas,
        [canvasId]: canvasData,
      },
    }));
  },

  mergeSupabaseInstances: (canvasId, componentFileKey, supabaseData) => {
    set((state) => {
      const canvasData = state.instancesByCanvas[canvasId] ?? {};
      const existingComponentData = canvasData[componentFileKey] ?? {};

      // Build merged component data
      const mergedComponentData: Record<string, Record<string, Instance>> = {
        ...existingComponentData,
      };

      for (const [storyboardId, supabaseInstances] of Object.entries(
        supabaseData,
      )) {
        const existingStoryboardData =
          existingComponentData[storyboardId] ?? {};
        const mergedStoryboardData: Record<string, Instance> = {
          ...existingStoryboardData,
        };

        for (const [instanceKey, supabaseInstance] of Object.entries(
          supabaseInstances,
        )) {
          const existingInstance = existingStoryboardData[instanceKey];

          if (existingInstance) {
            // Instance exists in store (from NavTree) - only merge captured data
            // NavTree data is "fresh", Supabase is "stale" for identity fields
            mergedStoryboardData[instanceKey] = {
              ...existingInstance,
              contentWindowData: {
                ...supabaseInstance.contentWindowData,
                ...existingInstance.contentWindowData,
              },
              dragData: {
                ...supabaseInstance.dragData,
                ...existingInstance.dragData,
              },
            };
          } else {
            // Instance doesn't exist - add from Supabase
            // Will be overwritten by NavTree if instance still exists in DOM
            mergedStoryboardData[instanceKey] = supabaseInstance;
          }
        }

        mergedComponentData[storyboardId] = mergedStoryboardData;
      }

      return {
        instancesByCanvas: {
          ...state.instancesByCanvas,
          [canvasId]: {
            ...canvasData,
            [componentFileKey]: mergedComponentData,
          },
        },
      };
    });
  },
}));
